import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { BookMarked, Trash2, Sparkles, ExternalLink, Quote, FileAudio, RefreshCw, X, Check, Search, PenLine, Instagram, Youtube, Facebook, Linkedin, Music2, Megaphone, AtSign } from "lucide-react";
import { CtaButton, MonoLabel, SecondaryButton } from "../components/kit";
import { AddToCalendar } from "../components/AddToCalendar";
import api, { formatApiErrorDetail } from "../lib/api";

const PLATFORM_ICONS = { TIKTOK: Music2, INSTAGRAM: Instagram, YOUTUBE: Youtube, FACEBOOK: Facebook, LINKEDIN: Linkedin, X: AtSign, OTHER: AtSign, "ADS LIBRARY": Megaphone };

const BeatText = ({ beat }) => {
  const i = beat.indexOf(":");
  if (i === -1) return <p className="text-[12.5px] text-[#181621] leading-snug">{beat}</p>;
  return (
    <p className="text-[12.5px] text-[#181621] leading-snug">
      <span className="font-bold">{beat.slice(0, i + 1)}</span>{beat.slice(i + 1)}
    </p>
  );
};

const Breakdown = ({ b }) => (
  <div className="mt-3 space-y-4">
    <div className="rounded-r-lg border-l-[3px] border-l-[#E0603A] bg-[#FAF8F3] px-3.5 py-3">
      <p className="text-[9.5px] font-bold tracking-[0.12em] text-[#E0603A] uppercase flex items-center gap-1"><Quote size={9} /> Hook</p>
      <p className="text-[13px] italic font-medium text-[#181621] mt-1 leading-snug">{b.hook_quote}</p>
    </div>
    <div>
      <p className="text-[9.5px] font-bold tracking-[0.12em] text-[#9A9591] uppercase">Structure</p>
      <ol className="mt-2">
        {(b.structure_beats || []).map((beat, i, arr) => (
          <li key={i} className="relative pl-8 pb-3 last:pb-0">
            {i < arr.length - 1 && <span className="absolute left-[10px] top-6 bottom-0 w-px bg-[#EAE5DB]" aria-hidden="true" />}
            <span className="absolute left-0 top-0 w-[21px] h-[21px] rounded-full border-[1.5px] border-[#E0603A] bg-white text-[10px] font-bold text-[#E0603A] flex items-center justify-center tabular-nums" aria-hidden="true">{i + 1}</span>
            <BeatText beat={beat} />
          </li>
        ))}
      </ol>
    </div>
    <div>
      <p className="text-[9.5px] font-bold tracking-[0.12em] text-[#9A9591] uppercase">Why it works</p>
      <p className="text-[12.5px] text-[#54505B] mt-1 leading-snug">{b.why_it_works}</p>
    </div>
    <div className="grid grid-cols-2 gap-3">
      <div className="rounded-lg p-3" style={{ background: "rgba(62,123,62,.05)", border: "1px solid rgba(62,123,62,.35)" }}>
        <p className="text-[9.5px] font-bold tracking-[0.12em] text-[#3E7B3E] uppercase flex items-center gap-1"><Check size={10} strokeWidth={2.5} /> Steal</p>
        <ul className="mt-1.5 space-y-1">
          {(b.steal || []).map((x, i) => (
            <li key={i} className="text-[12px] text-[#181621] leading-snug flex gap-1.5"><span className="text-[#3E7B3E] shrink-0">✓</span>{x}</li>
          ))}
        </ul>
      </div>
      <div className="rounded-lg p-3" style={{ background: "rgba(224,96,58,.05)", border: "1px solid rgba(224,96,58,.35)" }}>
        <p className="text-[9.5px] font-bold tracking-[0.12em] text-[#E0603A] uppercase flex items-center gap-1"><X size={10} strokeWidth={2.5} /> Avoid</p>
        <ul className="mt-1.5 space-y-1">
          {(b.avoid || []).map((x, i) => (
            <li key={i} className="text-[12px] text-[#181621] leading-snug flex gap-1.5"><span className="text-[#E0603A] shrink-0">✕</span>{x}</li>
          ))}
        </ul>
      </div>
    </div>
  </div>
);

const RefCard = ({ r, onDelete, onAnalyze, analyzing, onScript, calAdded, calBusy, onAddCal }) => {
  const isAd = r.source === "ads_spy";
  const badge = isAd ? "ADS LIBRARY" : r.platform || "OTHER";
  const PIcon = PLATFORM_ICONS[badge] || AtSign;
  const link = r.url || r.landing_url;
  return (
    <div className="card-soft p-5 flex flex-col" data-testid={`ref-card-${r.id}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-[#FBFAF7] border border-[#EAE5DB] px-2.5 py-1 text-[10.5px] font-bold uppercase tracking-[0.05em] text-[#181621]">
            <PIcon size={11} strokeWidth={2} /> {badge}
          </span>
          {r.breakdown?.format_name && (
            <span className="inline-flex items-center rounded-full border border-[#EAE5DB] bg-white px-2.5 py-1 text-[10.5px] font-bold uppercase tracking-[0.05em] text-[#54505B]" data-testid={`ref-format-${r.id}`}>{r.breakdown.format_name}</span>
          )}
          {isAd && r.page_name && <span className="text-[12px] font-bold text-[#54505B]">{r.page_name}</span>}
        </div>
        <div className="flex items-center gap-2.5 shrink-0">
          {link && <a href={link} target="_blank" rel="noreferrer" className="text-[#9A9591] hover:text-[#181621]" data-testid={`ref-link-${r.id}`}><ExternalLink size={13} /></a>}
          <button onClick={() => onDelete(r)} className="text-[#9A9591] hover:text-[#B4552D]" data-testid={`ref-delete-${r.id}`}><Trash2 size={14} strokeWidth={1.8} /></button>
        </div>
      </div>
      {!r.breakdown && (
        <p className="text-[12.5px] text-[#54505B] mt-3 leading-snug line-clamp-3 whitespace-pre-line">{r.ad_copy || r.transcript}</p>
      )}
      {r.breakdown ? (
        <Breakdown b={r.breakdown} />
      ) : (
        <SecondaryButton className="!py-1.5 !px-3 !text-[12px] mt-3 flex items-center gap-1.5 self-start" onClick={() => onAnalyze(r)} disabled={analyzing} data-testid={`ref-analyze-${r.id}`}>
          <Sparkles size={12} /> {analyzing ? "Breaking it down…" : "Analyze structure"}
        </SecondaryButton>
      )}
      {r.note && <p className="text-[11.5px] text-[#9A9591] mt-3 italic border-t border-[#F3EFE7] pt-2.5" data-testid={`ref-note-${r.id}`}>Note: {r.note}</p>}
      <p className="text-[10.5px] text-[#C9C6BE] mt-2 tabular-nums">{(r.created_at || "").slice(0, 10)}</p>
      {r.breakdown && (
        <div className="flex flex-wrap gap-2 mt-3.5 pt-3.5 border-t border-[#F3EFE7]">
          <CtaButton className="!py-1.5 !px-3.5 !text-[12px]" onClick={() => onScript(r)} data-testid={`ref-to-script-${r.id}`}>
            <PenLine size={12} strokeWidth={1.8} /> Turn it into a script
          </CtaButton>
          <AddToCalendar added={calAdded} busy={calBusy} onAdd={(date) => onAddCal(r, date)} testId={`ref-add-plan-${r.id}`} />
        </div>
      )}
    </div>
  );
};

export default function ReferenceLab() {
  const [refs, setRefs] = useState(undefined);
  const [videoUrl, setVideoUrl] = useState("");
  const [note, setNote] = useState("");
  const [analyzingId, setAnalyzingId] = useState(null);
  const [search, setSearch] = useState("");
  const [calOrigins, setCalOrigins] = useState({});
  const [calBusyId, setCalBusyId] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [progressStep, setProgressStep] = useState(""); // "" | "checking" | "retrieving" | "transcribing" | "analysing" | "done"
  const [errorMsg, setErrorMsg] = useState("");
  const pollRef = useRef(null);
  const navigate = useNavigate();

  const load = () => api.get("/references").then(({ data }) => setRefs(data)).catch(() => setRefs([]));
  const loadCalendar = () =>
    api.get("/calendar").then(({ data }) => {
      const m = {};
      (data.slots || []).forEach((s) => { if (s.origin?.startsWith("ref:")) m[s.origin.slice(4)] = true; });
      setCalOrigins(m);
    }).catch(() => {});
  useEffect(() => { load(); loadCalendar(); return () => { if (pollRef.current) clearInterval(pollRef.current); }; }, []);

  const validUrl = /^https?:\/\/[^\s]+$/i.test(videoUrl.trim());

  const stepLabels = {
    checking: "Checking video link…",
    retrieving: "Retrieving video…",
    transcribing: "Transcribing content…",
    analysing: "Analysing reference…",
    done: "Reference ready",
  };

  const stopPolling = () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };

  const analyseVideo = async () => {
    if (processing || !validUrl) return;
    setErrorMsg("");
    setProcessing(true);
    setProgressStep("checking");
    try {
      const { data } = await api.post("/references/from-url", { video_url: videoUrl.trim(), note: note.trim() });
      const jobId = data.job;
      if (!jobId) throw new Error("no_job");
      // Poll status every 2 s. Server dedupes any duplicate submission.
      pollRef.current = setInterval(async () => {
        try {
          const { data: s } = await api.get(`/references/from-url/${jobId}`);
          setProgressStep(s.state || "checking");
          if (s.state === "done") {
            stopPolling();
            setProcessing(false);
            setVideoUrl(""); setNote("");
            setProgressStep("");
            toast("Reference saved & broken down.", { description: "Now use it in the Organic Studio to replicate its structure." });
            load();
          } else if (s.state === "failed") {
            stopPolling();
            setProcessing(false);
            setProgressStep("");
            setErrorMsg(s.error || "We couldn't access this video. Check that the link is public and try again.");
          }
        } catch (err) {
          if (err.response && err.response.status === 404) {
            stopPolling();
            setProcessing(false);
            setProgressStep("");
            setErrorMsg("The job could not be tracked. Please try again.");
          }
          // Otherwise keep polling — transient network errors shouldn't kill the UI.
        }
      }, 2000);
    } catch (err) {
      setProcessing(false);
      setProgressStep("");
      const detail = formatApiErrorDetail(err.response?.data?.detail);
      setErrorMsg(detail || "We couldn't access this video. Check that the link is public and try again.");
    }
  };

  const analyze = async (r) => {
    setAnalyzingId(r.id);
    try {
      await api.post(`/references/${r.id}/analyze`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setAnalyzingId(null);
    }
  };

  const remove = async (r) => {
    if (!window.confirm("Delete this reference?")) return;
    await api.delete(`/references/${r.id}`);
    load();
  };

  const toScript = (r) => navigate("/app/organic/scripts", { state: { referencePrefill: r.id } });

  const addToCal = async (r, date) => {
    setCalBusyId(r.id);
    try {
      await api.post("/calendar/slots", {
        title: r.breakdown?.hook_quote?.slice(0, 120) || r.breakdown?.format_name || "Reference",
        date,
        tipo: "topic",
        punti: r.breakdown?.why_it_works || "",
        cta: "",
        status: "idea",
        origin: `ref:${r.id}`,
      });
      setCalOrigins((m) => ({ ...m, [r.id]: true }));
      toast("Added to your editorial calendar.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't add it.");
      loadCalendar();
    } finally {
      setCalBusyId(null);
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return refs || [];
    return (refs || []).filter((r) =>
      [r.breakdown?.hook_quote, r.breakdown?.format_name, r.note, r.page_name, r.platform, r.ad_copy, r.transcript]
        .filter(Boolean).some((t) => t.toLowerCase().includes(q))
    );
  }, [refs, search]);

  return (
    <div className="p-6 sm:p-10 max-w-5xl" data-testid="reference-lab-page">
      <MonoLabel>Brainstorming Area · Reference Lab</MonoLabel>
      <h1 className="mt-5 text-3xl sm:text-4xl leading-tight font-extrabold tracking-[-0.02em]">
        Save what works, steal the structure — never the content
      </h1>

      <div className="card-soft mt-8 p-6 sm:p-7" data-testid="ref-collect-card">
        <p className="text-[11px] font-bold tracking-[0.08em] text-[#9A9591] uppercase">Add a reference</p>
        <div className="mt-4 space-y-4">
          <div>
            <label className="text-[11px] font-semibold text-[#9A9591] block mb-1.5" htmlFor="ref-video-url">Video link</label>
            <input
              id="ref-video-url"
              className="field-input"
              type="url"
              inputMode="url"
              placeholder="Paste a public video URL"
              value={videoUrl}
              onChange={(e) => { setVideoUrl(e.target.value); setErrorMsg(""); }}
              disabled={processing}
              data-testid="ref-video-url-input"
            />
            <p className="text-[11px] text-[#9A9591] mt-1.5">Add a public video link. Agenzee will retrieve, transcribe and analyse the content automatically.</p>
          </div>
          <div>
            <label className="text-[11px] font-semibold text-[#9A9591] block mb-1.5" htmlFor="ref-note">Personal note (optional)</label>
            <input
              id="ref-note"
              className="field-input"
              placeholder='e.g. "love the pacing of the middle part"'
              value={note}
              onChange={(e) => setNote(e.target.value)}
              disabled={processing}
              data-testid="ref-note-input"
            />
          </div>
        </div>

        {processing && (
          <div
            className="rounded-xl border border-[#EAE5DB] bg-[#FAF8F3] px-3.5 py-3 mt-4 flex items-center gap-3"
            role="status"
            aria-live="polite"
            aria-busy="true"
            data-testid="ref-progress"
          >
            <span className="w-2 h-2 rounded-full bg-[#96691E] inline-block animate-pulse" aria-hidden="true" />
            <p className="text-[12.5px] font-semibold text-[#181621]" data-testid="ref-progress-label">
              {stepLabels[progressStep] || stepLabels.checking}
            </p>
          </div>
        )}
        {errorMsg && !processing && (
          <div
            className="rounded-xl border-l-[3px] border-l-[#E0603A] border border-[#F2D8CE] bg-white px-3.5 py-2.5 mt-4 flex items-start gap-2.5"
            role="alert"
            data-testid="ref-error"
          >
            <p className="text-[12.5px] text-[#B4552D] font-semibold flex-1">{errorMsg}</p>
            <button onClick={() => setErrorMsg("")} className="text-[#B4552D] hover:text-[#181621]" aria-label="Dismiss error" data-testid="ref-error-dismiss">
              <X size={13} />
            </button>
          </div>
        )}

        <CtaButton
          className="mt-4"
          onClick={analyseVideo}
          disabled={processing || !validUrl}
          data-testid="ref-analyse-btn"
        >
          <BookMarked size={14} /> {processing ? "Analysing…" : "Analyse video"}
        </CtaButton>
      </div>

      <div className="mt-8">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <p className="text-[11px] font-bold tracking-[0.08em] text-[#9A9591] uppercase tabular-nums">Your library {refs ? `— ${refs.length}` : ""}</p>
          <div className="relative">
            <Search size={13} strokeWidth={1.8} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9A9591]" />
            <input
              className="field-input !py-2 !pl-8 !text-[12.5px] w-[220px]"
              placeholder="Search hooks, formats, notes…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="ref-search-input"
            />
          </div>
        </div>
        {refs === undefined && <div className="card-soft h-32 mt-4 animate-pulse" />}
        {refs && !refs.length && (
          <div className="card-soft p-10 mt-4 text-center" data-testid="ref-empty">
            <p className="font-bold text-[15px] text-[#181621]">Nothing saved yet.</p>
            <p className="text-[12.5px] text-[#54505B] mt-1.5">Add a content piece above — or send competitor ads here from Ads Spy.</p>
          </div>
        )}
        {refs && refs.length > 0 && filtered.length === 0 && (
          <p className="text-[13px] text-[#54505B] mt-4 text-center" data-testid="ref-search-empty">No references match “{search}”.</p>
        )}
        <div className="grid sm:grid-cols-2 gap-5 mt-4" data-testid="ref-library">
          {filtered.map((r) => (
            <RefCard
              key={r.id} r={r} onDelete={remove} onAnalyze={analyze} analyzing={analyzingId === r.id}
              onScript={toScript} calAdded={!!calOrigins[r.id]} calBusy={calBusyId === r.id} onAddCal={addToCal}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
